import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GlobalDataService } from './global-data.service';
import { server } from './service.constants';
import { VaultService } from './vault.service';
import { map, take } from 'rxjs';
import { TRANSACTION_ID, USER_OBJECT } from './vault-service.constants';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  constructor(
    private http: HttpClient,
    public globalData: GlobalDataService,
    private vaultService: VaultService
  ) {}
  async getService(url: string) {
    return this.http.get(
      server.getValue() + url,
      await this.getHeader(url, 'GET')
    );
  }
  async getServicePDF(url: string) {
    if (this.globalData.refreshTokenPromise) {
      try { await this.globalData.refreshTokenPromise; } catch { /* proceed */ }
    }
    const userObject = await this.vaultService.getKeyValue(USER_OBJECT);
    return this.http.get(
      server.getValue() + url,
      {
        headers: {
          Authorization: 'Bearer ' + userObject?.authToken,
        },
        responseType: 'blob'
      }
    );
  }
  async getServiceWithoutToken(url: string) {
    return this.http
      .get(server.getValue() + url, await this.getHeader(url, 'GET'))
      .pipe(take(1));
  }
  getExternalService(url: string) {
    const headers_object = new HttpHeaders({
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
    });
    const httpOptions = {
      headers: headers_object,
    };
    return this.http.get(url, httpOptions);
  }

  async getServiceWithBody(url: string, obj: any) {
    if (this.globalData.refreshTokenPromise) {
      try { await this.globalData.refreshTokenPromise; } catch { /* proceed */ }
    }
    const userObject = await this.vaultService.getKeyValue(USER_OBJECT);
    const headers_object = new HttpHeaders({
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
      Authorization: 'Bearer ' + userObject?.authToken,
    });
    const httpOptions = {
      headers: headers_object,
      body: obj,
    };
    return this.http.get(server.getValue() + url, httpOptions);
  }

  async authenticate(url: string, loginObject: any) {
    return this.http
      .post(
        server.getValue() + url,
        loginObject,
        await this.getHeader(url, 'POST')
      )
      .pipe(take(1));
  }

  async postServiceWithNoToken(url: string, reqObject: any) {
    return this.http
      .post(
        server.getValue() + url,
        reqObject,
        await this.getHeader(url, 'POST')
      )
      .pipe(take(1));
  }
  async postTokenRefreshService(url: string, reqObject: { refreshToken: any }) {
    return this.http
      .post(
        server.getValue() + url,
        reqObject,
        await this.getHeader(url, 'POST')
      )
      .pipe(take(1));
  }

  async postService(url: string, reqObject: any, platform?: string) {
    return this.http
      .post(
        server.getValue() + url,
        reqObject,
        platform ? await this.getUploadHeader(url, 'POST', platform) : await this.getHeader(url, 'POST') 
      )
      .pipe(take(1));
  }

  async get(url: string) {
    let headers: any = await this.getHeader(url, 'GET');
    headers.observe = 'response';
    headers.responseType = 'text' as 'json';
    return this.http.get(server.getValue() + url, headers);
  }
  async post(url: any, reqObject: any) {
    let headers: any = await this.getHeader(url, 'POST');

    return this.http.post(server.getValue() + url, reqObject, headers);
  }

  async getHeader(url: any, allowMethod: string) {
    // If a token refresh is already in flight (triggered by the checkTimeOut
    // listener on the same user interaction), wait for it to finish before
    // reading the vault. Without this, the API call races the refresh and
    // sends the old expired token → intermittent 400/401.
    if (this.globalData.refreshTokenPromise) {
      try { await this.globalData.refreshTokenPromise; } catch { /* proceed */ }
    }

    if (url.indexOf('/token/refresh') > 0) {
      const userObject = await this.vaultService.getKeyValue(USER_OBJECT);
      return {
        headers: new HttpHeaders({
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': allowMethod,
          'Content-Type': 'application/json',
          'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
          Authorization: 'Bearer ' + userObject?.refreshToken,
        }),
      };
    } else if (url.indexOf('/public/') > 0) {
      const txId = await this.vaultService.getKeyValue(TRANSACTION_ID);
      return {
        headers: new HttpHeaders({
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': allowMethod,
          'Content-Type': 'application/json',
          'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
          Authorization: 'Bearer ' + environment.dcssPublicApiToken,
          'X-DCSS-Transaction-Value': txId ?? ' ',
        }),
      };
    } else {
      const [userObject, txId] = await Promise.all([
        this.vaultService.getKeyValue(USER_OBJECT),
        this.vaultService.getKeyValue(TRANSACTION_ID),
      ]);
      return {
        headers: new HttpHeaders({
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': allowMethod,
          'Content-Type': 'application/json',
          'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
          Authorization: 'Bearer ' + userObject?.authToken,
          'X-DCSS-Transaction-Value': txId ?? ' ',
        }),
      };
    }
  }
  async getUploadHeader(url: any, allowMethod: string, platform: string) {
    if (this.globalData.refreshTokenPromise) {
      try { await this.globalData.refreshTokenPromise; } catch { /* proceed */ }
    }

    if (url.indexOf('/token/refresh') > 0) {
      const userObject = await this.vaultService.getKeyValue(USER_OBJECT);
      return {
        headers: new HttpHeaders({
          'platform': platform,
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': allowMethod,
          'Content-Type': 'application/json',
          'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
          Authorization: 'Bearer ' + userObject?.refreshToken,
        }),
      };
    } else {
      const [userObject, txId] = await Promise.all([
        this.vaultService.getKeyValue(USER_OBJECT),
        this.vaultService.getKeyValue(TRANSACTION_ID),
      ]);
      return {
        headers: new HttpHeaders({
          'platform': platform,
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': allowMethod,
          'Content-Type': 'application/json',
          'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
          Authorization: 'Bearer ' + userObject?.authToken,
          'X-DCSS-Transaction-Value': txId ?? ' ',
        }),
      };
    }
  }
};
